import { apiErrorMessage } from '@agos/api-client'
import { customFetch } from '@agos/api-client'
import {
  getGetAuthMeProfileQueryKey,
  useDeleteAuthMeAvatar,
  useGetAttendanceSelfHistory,
  useGetAuthMeActivity,
  useGetAuthMeProfile,
  useGetAuthMeStats,
  usePatchAuthMeProfile,
  usePostAuthMeLogoutAll,
  usePostAuthMePassword,
} from '@agos/api-client/hr'
import { useGetSettingsBasicPublic } from '@agos/api-client/platform'
import { T, formatCents, formatCount, formatDay, formatTime, toDateParam, tt, useAuth } from '@agos/shared'
import {
  BUTTON_GHOST,
  BUTTON_PRIMARY,
  cn,
  ConfirmDialog,
  EmptyState,
  PageHeader,
  PhoneInput,
  Spinner,
  StatCard,
  StatusBadge,
  TabGroup,
  useToast,
} from '@agos/ui'
import { useQueryClient } from '@tanstack/react-query'
import {
  Activity,
  AlertTriangle,
  Camera,
  CheckCircle2,
  Clock,
  CreditCard,
  Eye,
  EyeOff,
  History,
  Lock,
  LogIn,
  LogOut,
  Save,
  Shield,
  ShoppingBag,
  Trash2,
  TrendingUp,
  User as UserIcon,
  Wallet,
} from 'lucide-react'
import { useMemo, useRef, useState } from 'react'

import {
  BIO_LIMIT,
  actionColor,
  actionLabel,
  activityOf,
  attendanceNote,
  attributionNote,
  averageShiftMinutes,
  canChangePassword,
  canSave,
  displayName,
  formOf,
  formatMinutes,
  initialsOf,
  instantShifts,
  isDirty,
  isNamedAction,
  languagesNote,
  languagesOf,
  moneyLabel,
  orderCountLabel,
  ordersAreAttributed,
  passwordProblemMessage,
  passwordProblems,
  passwordStrength,
  performanceOf,
  performancePayments,
  pickerLanguages,
  problemsOf,
  profileOf,
  profilePayload,
  realShifts,
  recordsOf,
  relativeTime,
  revenuePerHourCents,
  revenuePerShiftCents,
  roleLabel,
  shiftMinutes,
  shiftState,
  strengthLabel,
  summaryOf,
  unnamedActions,
  verificationWouldReset,
  type AttendanceRecord,
  type PasswordForm,
  type Profile,
  type ProfileForm,
  type Range,
} from './profile'

/**
 * `/profile` — the only page every signed-in person may open, whatever their role.
 *
 * Ported from legacy `pages/profile.tsx` (1 098 lines). Four tabs: personal details with the avatar, the
 * password and sessions, one's own takings, and one's own attendance.
 *
 * **FB-175.** *Môj výkon* reports nought euros an hour to nine of the twenty-three staff. See `profile.ts`.
 *
 * **FB-176.** A 67-millisecond record counts as a completed shift and halves the average.
 *
 * **FB-177.** The language picker offers three where the server publishes four.
 *
 * **FB-178.** Six of the fifty newest activity entries render their raw database key.
 *
 * The avatar upload goes through `customFetch` directly rather than the generated `postAuthMeAvatar`, which
 * does `body: JSON.stringify(file)` under a JSON content-type and would send `{}` — the same spec defect as
 * FB-165's APK upload, filed the same way. Still `@agos/api-client`, so §6 holds.
 */

type TabKey = 'personal' | 'security' | 'performance' | 'attendance'

const TABS: readonly TabKey[] = ['personal', 'security', 'performance', 'attendance']

function tabLabel(key: TabKey): string {
  if (key === 'personal') return tt('Osobné údaje')
  if (key === 'security') return tt('Bezpečnosť')
  if (key === 'performance') return tt('Môj výkon')
  return tt('Moja dochádzka')
}

function startOfMonth(): string {
  const now = new Date()
  return toDateParam(new Date(now.getFullYear(), now.getMonth(), 1))
}

export function ProfilePage() {
  const [tab, setTab] = useState<TabKey>('personal')
  const { data, isLoading } = useGetAuthMeProfile()
  const profile = useMemo(() => profileOf(data), [data])

  return (
    <div className="space-y-5">
      <PageHeader
        title={tt('Môj profil')}
        description={tt('Osobné údaje, bezpečnosť a dochádzka - všetko na jednom mieste.')}
        icon={<UserIcon className="w-5 h-5" />}
      />

      <TabGroup
        tabs={TABS.map((key) => ({ id: key, label: tabLabel(key) }))}
        active={tab}
        onChange={(id) => setTab(id as TabKey)}
        variant="bar"
      />

      {isLoading ? (
        <div className="flex justify-center py-16">
          <Spinner />
        </div>
      ) : profile === null ? (
        <EmptyState
          icon={<UserIcon className="w-8 h-8" />}
          title={tt('Profil sa nenačítal')}
          description={tt('Skúste obnoviť stránku.')}
        />
      ) : (
        <>
          {tab === 'personal' && <PersonalTab profile={profile} />}
          {tab === 'security' && <SecurityTab />}
          {tab === 'performance' && <PerformanceTab role={profile.systemRole} />}
          {tab === 'attendance' && <AttendanceTab />}
        </>
      )}
    </div>
  )
}

// ─── personal ────────────────────────────────────────────────────────────────

function PersonalTab({ profile }: { profile: Profile }) {
  const { toast } = useToast()
  const { refreshUser } = useAuth()
  const queryClient = useQueryClient()
  const fileInput = useRef<HTMLInputElement>(null)

  const [form, setForm] = useState<ProfileForm>(() => formOf(profile))
  const [uploading, setUploading] = useState(false)
  const [removingAvatar, setRemovingAvatar] = useState(false)

  const settings = useGetSettingsBasicPublic()
  const published = useMemo(() => languagesOf(settings.data), [settings.data])
  const options = pickerLanguages(published, form.language)
  const problems = problemsOf(form, published)

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: getGetAuthMeProfileQueryKey() })
    void refreshUser()
  }

  const save = usePatchAuthMeProfile({
    mutation: {
      onSuccess: () => {
        refresh()
        toast({ title: tt('Uložené'), description: tt('Váš profil bol aktualizovaný.') })
      },
      // §9.385 - the profile endpoint refuses a taken e-mail and a bad phone by name.
      onError: (caught) =>
        toast({
          variant: 'destructive',
          title: tt('Uloženie zlyhalo'),
          description: apiErrorMessage(caught, tt('Server zmenu odmietol.')),
        }),
    },
  })

  const removeAvatar = useDeleteAuthMeAvatar({
    mutation: {
      onSuccess: () => {
        setRemovingAvatar(false)
        refresh()
        toast({ title: tt('Obrázok odstránený') })
      },
      onError: (caught) => {
        setRemovingAvatar(false)
        toast({
          variant: 'destructive',
          title: tt('Obrázok sa nepodarilo odstrániť'),
          description: apiErrorMessage(caught, tt('Server odstránenie odmietol.')),
        })
      },
    },
  })

  const uploadAvatar = async (file: File) => {
    setUploading(true)
    try {
      const body = new FormData()
      body.append('avatar', file)
      // Not the generated hook: it stringifies the body under a JSON content-type. Letting the browser set
      // the multipart boundary is the whole point, so no `Content-Type` is passed here.
      await customFetch('/auth/me/avatar', { method: 'POST', body })
      refresh()
      toast({ title: tt('Obrázok uložený') })
    } catch {
      toast({ variant: 'destructive', title: tt('Obrázok sa nepodarilo nahrať') })
    } finally {
      setUploading(false)
      if (fileInput.current !== null) fileInput.current.value = ''
    }
  }

  const shown = displayName(form, profile.name)

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-5">
      <div className="bg-card border border-border rounded-2xl p-5 flex flex-col items-center text-center">
        <div className="relative">
          {profile.avatarUrl === null ? (
            <div
              className="w-32 h-32 rounded-full bg-gradient-to-tr from-primary to-orange-400 flex items-center justify-center text-white text-4xl font-black shadow-md ring-2 ring-primary/30"
              data-initials
            >
              {initialsOf(shown)}
            </div>
          ) : (
            <div className="w-32 h-32 rounded-full overflow-hidden ring-2 ring-primary/20 bg-card">
              <img src={profile.avatarUrl} alt={shown} className="w-full h-full object-cover" />
            </div>
          )}
          <button
            type="button"
            data-pick-avatar
            disabled={uploading}
            onClick={() => fileInput.current?.click()}
            aria-label={tt('Zmeniť profilový obrázok')}
            className="absolute -bottom-1 -right-1 w-10 h-10 rounded-full bg-primary text-primary-foreground border-4 border-card flex items-center justify-center shadow-lg disabled:opacity-50"
          >
            {uploading ? <Spinner className="w-4 h-4" /> : <Camera className="w-4 h-4" />}
          </button>
          <input
            ref={fileInput}
            type="file"
            accept="image/*"
            hidden
            aria-label={tt('Profilový obrázok')}
            onChange={(event) => {
              const file = event.target.files?.[0]
              if (file !== undefined) void uploadAvatar(file)
            }}
          />
        </div>

        <h2 className="mt-4 text-lg font-black leading-tight" data-display-name>
          {shown}
        </h2>
        <p className="text-xs text-muted-foreground mt-0.5">{profile.position}</p>

        <div className="mt-2 flex flex-wrap gap-1 justify-center">
          <StatusBadge label={roleLabel(profile.systemRole)} color="amber" />
          {profile.isExternal && <StatusBadge label={tt('Externý')} color="orange" />}
          {profile.emailVerified ? (
            <StatusBadge label={tt('E-mail overený')} color="green" icon={CheckCircle2} />
          ) : profile.email !== null ? (
            <StatusBadge label={tt('E-mail neoverený')} color="amber" icon={AlertTriangle} />
          ) : null}
        </div>

        {profile.avatarUrl !== null && (
          <button
            type="button"
            data-remove-avatar
            onClick={() => setRemovingAvatar(true)}
            className="mt-4 text-xs text-muted-foreground hover:text-red-400 inline-flex items-center gap-1.5"
          >
            <Trash2 className="w-3 h-3" /> <T>Odstrániť obrázok</T>
          </button>
        )}

        {profile.createdAt !== null && (
          <p className="text-[10px] text-muted-foreground/60 mt-4">
            <T>V tíme od</T> {formatDay(profile.createdAt)}
          </p>
        )}
      </div>

      <div className="bg-card border border-border rounded-2xl p-5 space-y-5">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label={tt('Meno')}>
            <input
              value={form.firstName}
              onChange={(event) => setForm({ ...form, firstName: event.target.value })}
              aria-label={tt('Meno')}
              maxLength={80}
              className={INPUT}
            />
          </Field>
          <Field label={tt('Priezvisko')}>
            <input
              value={form.lastName}
              onChange={(event) => setForm({ ...form, lastName: event.target.value })}
              aria-label={tt('Priezvisko')}
              maxLength={80}
              className={INPUT}
            />
          </Field>
          <Field label={tt('E-mail')}>
            <input
              value={form.email}
              onChange={(event) => setForm({ ...form, email: event.target.value })}
              aria-label={tt('E-mail')}
              maxLength={200}
              className={INPUT}
            />
            {verificationWouldReset(form, profile) && (
              <p className="text-[11px] text-amber-400 mt-1 flex items-center gap-1" data-verification-warning>
                <AlertTriangle className="w-3 h-3" /> <T>Po zmene e-mailu sa overenie zruší.</T>
              </p>
            )}
          </Field>
          <Field label={tt('Telefón')}>
            <PhoneInput
              value={form.phone}
              onChange={(next) => setForm({ ...form, phone: next })}
              label={tt('Telefón')}
              testId="profile"
            />
          </Field>
        </div>

        {/* FB-177 - the list comes from the server, and says so when it did not arrive. */}
        <Field label={tt('Jazyk rozhrania')}>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-1" data-languages={options.length}>
            {options.map((code) => (
              <button
                key={code}
                type="button"
                data-language={code}
                data-language-active={form.language === code ? 'yes' : 'no'}
                onClick={() => setForm({ ...form, language: code })}
                className={cn(
                  'py-2 rounded-lg border text-xs font-bold transition-colors',
                  form.language === code
                    ? 'bg-primary/15 border-primary/40 text-foreground'
                    : 'bg-background border-border text-muted-foreground hover:text-foreground',
                )}
              >
                {code.toUpperCase()}
              </button>
            ))}
          </div>
          <p className="text-[10px] text-muted-foreground/60 mt-1" data-languages-note>
            {languagesNote(published, form.language)}
          </p>
        </Field>

        <Field label={tt('O mne')}>
          <textarea
            value={form.bio}
            onChange={(event) => setForm({ ...form, bio: event.target.value })}
            rows={3}
            aria-label={tt('O mne')}
            className={cn(INPUT, 'resize-none')}
          />
          <p className="text-[10px] text-muted-foreground/60 mt-1 text-right" data-bio-count>
            {formatCount(form.bio.length)} / {formatCount(BIO_LIMIT)}
          </p>
        </Field>

        {problems.length > 0 && (
          <div className="space-y-1" data-profile-problems>
            {problems.map((problem) => (
              <p key={problem.kind} className="flex items-start gap-1.5 text-xs text-red-500">
                <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-px" />
                {problem.kind === 'bad-email' ? (
                  <T>Toto nevyzerá ako e-mailová adresa.</T>
                ) : problem.kind === 'bio-too-long' ? (
                  <T>Popis je dlhší, než server prijme.</T>
                ) : (
                  <T>Tento jazyk nie je medzi zapnutými.</T>
                )}
              </p>
            ))}
          </div>
        )}

        <div className="flex items-center gap-3 pt-2 border-t border-border">
          <button
            type="button"
            data-save-profile
            onClick={() => save.mutate({ data: profilePayload(form) })}
            disabled={save.isPending || !canSave(form, profile, published)}
            className={cn('flex items-center gap-2 px-6 py-2.5 rounded-xl', BUTTON_PRIMARY, 'text-sm')}
          >
            {save.isPending ? <Spinner className="w-4 h-4" /> : <Save className="w-4 h-4" />}
            <T>Uložiť zmeny</T>
          </button>
          {isDirty(form, profile) && (
            <span className="text-xs text-muted-foreground" data-dirty>
              <T>Neuložené zmeny</T>
            </span>
          )}
        </div>
      </div>

      <ConfirmDialog
        open={removingAvatar}
        onClose={() => setRemovingAvatar(false)}
        onConfirm={() => removeAvatar.mutate()}
        title={tt('Odstrániť profilový obrázok?')}
        description={tt('Namiesto obrázka sa zobrazia vaše iniciály.')}
        confirmLabel={tt('Odstrániť')}
        variant="danger"
        loading={removeAvatar.isPending}
      />
    </div>
  )
}

// ─── security ────────────────────────────────────────────────────────────────

function SecurityTab() {
  const { toast } = useToast()
  const { logout } = useAuth()

  const [form, setForm] = useState<PasswordForm>({ current: '', next: '', confirm: '' })
  const [showCurrent, setShowCurrent] = useState(false)
  const [showNext, setShowNext] = useState(false)
  const [confirmingLogoutAll, setConfirmingLogoutAll] = useState(false)

  const problems = passwordProblems(form)
  const score = passwordStrength(form.next)
  // A problem is only worth showing once the field it is about has been touched.
  const visible = problems.filter(
    (problem) =>
      (problem !== 'no-current' || form.next !== '') &&
      (problem !== 'too-short' || form.next !== '') &&
      (problem !== 'same-as-current' || form.next !== ''),
  )

  const change = usePostAuthMePassword({
    mutation: {
      onSuccess: () => {
        setForm({ current: '', next: '', confirm: '' })
        toast({
          title: tt('Heslo zmenené'),
          description: tt('Ostatné zariadenia sú odhlásené.'),
        })
      },
      // §9.385 - *pravdepodobne* was a guess, and a wrong one whenever the refusal is the new
      // password's own length or a rotation the policy forbids. The server says which.
      onError: (caught) =>
        toast({
          variant: 'destructive',
          title: tt('Heslo sa nezmenilo'),
          description: apiErrorMessage(caught, tt('Súčasné heslo pravdepodobne nesedí.')),
        }),
    },
  })

  const logoutAll = usePostAuthMeLogoutAll({
    mutation: {
      onSuccess: () => {
        setConfirmingLogoutAll(false)
        toast({ title: tt('Všetky relácie ukončené'), description: tt('Prihláste sa znova.') })
        void logout()
      },
      onError: (caught) => {
        setConfirmingLogoutAll(false)
        toast({
          variant: 'destructive',
          title: tt('Nepodarilo sa odhlásiť zariadenia'),
          description: apiErrorMessage(caught, tt('Server odhlásenie odmietol.')),
        })
      },
    },
  })

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
      <div className="bg-card border border-border rounded-2xl p-5 space-y-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-amber-500/15 text-amber-400 flex items-center justify-center">
            <Lock className="w-5 h-5" />
          </div>
          <div>
            <h3 className="font-black">
              <T>Zmena hesla</T>
            </h3>
            <p className="text-xs text-muted-foreground">
              <T>Po zmene budete odhlásený zo všetkých ostatných zariadení.</T>
            </p>
          </div>
        </div>

        <PasswordField
          label={tt('Aktuálne heslo')}
          value={form.current}
          show={showCurrent}
          onToggle={() => setShowCurrent(!showCurrent)}
          onChange={(value) => setForm({ ...form, current: value })}
          testId="current"
        />
        <PasswordField
          label={tt('Nové heslo (min. 6 znakov)')}
          value={form.next}
          show={showNext}
          onToggle={() => setShowNext(!showNext)}
          onChange={(value) => setForm({ ...form, next: value })}
          testId="next"
        />
        {form.next !== '' && (
          <div data-strength={score}>
            <div className="flex gap-1">
              {[0, 1, 2, 3, 4].map((index) => (
                <div
                  key={index}
                  className={cn(
                    'h-1.5 flex-1 rounded-full transition-colors',
                    index < score ? STRENGTH_COLORS[score] : 'bg-muted',
                  )}
                />
              ))}
            </div>
            <p className="text-[11px] text-muted-foreground mt-1">
              <T>Sila hesla</T>: <span className="font-bold text-foreground">{strengthLabel(score)}</span>
            </p>
          </div>
        )}
        <PasswordField
          label={tt('Potvrdenie nového hesla')}
          value={form.confirm}
          show={showNext}
          onToggle={() => setShowNext(!showNext)}
          onChange={(value) => setForm({ ...form, confirm: value })}
          testId="confirm"
        />

        {visible.length > 0 && (
          <div className="space-y-1" data-password-problems>
            {visible.map((problem) => (
              <p key={problem} className="text-[11px] text-red-400 flex items-center gap-1">
                <AlertTriangle className="w-3 h-3 shrink-0" />
                {passwordProblemMessage(problem)}
              </p>
            ))}
          </div>
        )}

        <button
          type="button"
          data-change-password
          onClick={() =>
            change.mutate({ data: { currentPassword: form.current, newPassword: form.next } })
          }
          disabled={change.isPending || !canChangePassword(form)}
          className={cn('w-full flex items-center justify-center gap-2 px-5 py-2.5 rounded-xl', BUTTON_PRIMARY, 'text-sm')}
        >
          {change.isPending ? <Spinner className="w-4 h-4" /> : <Save className="w-4 h-4" />}
          <T>Zmeniť heslo</T>
        </button>
      </div>

      <div className="space-y-5">
        <ActivityCard />

        <div className="bg-card border border-border rounded-2xl p-5 space-y-3">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-red-500/15 text-red-400 flex items-center justify-center">
              <LogOut className="w-5 h-5" />
            </div>
            <div>
              <h3 className="font-black">
                <T>Odhlásiť všetky zariadenia</T>
              </h3>
              <p className="text-xs text-muted-foreground">
                <T>Zruší všetky aktívne relácie vrátane tejto.</T>
              </p>
            </div>
          </div>
          <button
            type="button"
            data-logout-all
            onClick={() => setConfirmingLogoutAll(true)}
            className="w-full flex items-center justify-center gap-2 px-5 py-2.5 rounded-xl border-2 border-red-500/30 text-red-400 font-bold text-sm"
          >
            <LogOut className="w-4 h-4" /> <T>Odhlásiť všade</T>
          </button>
        </div>

        <div className="bg-card border border-border rounded-2xl p-5 space-y-3">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-emerald-500/15 text-emerald-400 flex items-center justify-center">
              <Shield className="w-5 h-5" />
            </div>
            <h3 className="font-black">
              <T>Tipy pre bezpečnosť</T>
            </h3>
          </div>
          <ul className="text-sm text-muted-foreground space-y-2">
            <li>
              <T>Použite unikátne heslo - také, ktoré nemáte nikde inde.</T>
            </li>
            <li>
              <T>Aspoň 10 znakov, veľké aj malé písmená, čísla a špeciálne znaky.</T>
            </li>
            <li>
              <T>Nikomu neukazujte svoje heslo - ani kolegom, ani vedeniu.</T>
            </li>
            <li>
              <T>Pri podozrení na únik si heslo okamžite zmeňte.</T>
            </li>
          </ul>
        </div>
      </div>

      <ConfirmDialog
        open={confirmingLogoutAll}
        onClose={() => setConfirmingLogoutAll(false)}
        onConfirm={() => logoutAll.mutate()}
        title={tt('Odhlásiť všetky zariadenia?')}
        description={tt('Ukončí sa aj táto relácia - heslo budete musieť zadať znova aj tu.')}
        confirmLabel={tt('Odhlásiť všade')}
        variant="danger"
        loading={logoutAll.isPending}
      />
    </div>
  )
}

const STRENGTH_COLORS: Record<number, string> = {
  0: 'bg-red-500',
  1: 'bg-red-400',
  2: 'bg-amber-400',
  3: 'bg-yellow-400',
  4: 'bg-emerald-400',
  5: 'bg-emerald-500',
}

function PasswordField({
  label,
  value,
  show,
  onToggle,
  onChange,
  testId,
}: {
  label: string
  value: string
  show: boolean
  onToggle: () => void
  onChange: (value: string) => void
  testId: string
}) {
  return (
    <label className="block">
      <span className="text-[11px] font-bold text-muted-foreground uppercase tracking-wider">{label}</span>
      <div className="relative mt-1">
        <input
          type={show ? 'text' : 'password'}
          value={value}
          onChange={(event) => onChange(event.target.value)}
          aria-label={label}
          data-password={testId}
          autoComplete="new-password"
          className="w-full px-3 py-2 pr-10 rounded-xl bg-background border border-border text-sm outline-none focus:ring-2 focus:ring-primary/20"
        />
        <button
          type="button"
          onClick={onToggle}
          aria-label={show ? tt('Skryť heslo') : tt('Zobraziť heslo')}
          className={cn(BUTTON_GHOST, 'absolute right-2 top-1/2 -translate-y-1/2 p-1.5')}
        >
          {show ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
        </button>
      </div>
    </label>
  )
}

function ActivityCard() {
  const { data } = useGetAuthMeActivity({ limit: 50 })
  const entries = useMemo(() => activityOf(data), [data])
  const unknown = unnamedActions(entries)
  // Stamped once when the feed arrives, not per render: `Date.now()` in a render body is impure, and a
  // list that re-reads the clock per row can disagree with itself mid-scroll.
  const [now] = useState(() => Date.now())

  return (
    <div className="bg-card border border-border rounded-2xl p-5 space-y-3" data-activity={entries.length}>
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-blue-500/15 text-blue-400 flex items-center justify-center">
          <History className="w-5 h-5" />
        </div>
        <div className="flex-1">
          <h3 className="font-black">
            <T>Posledná aktivita</T>
          </h3>
          <p className="text-xs text-muted-foreground">
            <T>Posledných</T> {formatCount(entries.length)} <T>vašich akcií v systéme</T>
          </p>
        </div>
      </div>

      {unknown.length > 0 && (
        <p className="text-[11px] text-amber-500" data-unknown-actions={unknown.length}>
          <T>Neznáme druhy akcií</T>: {unknown.join(', ')}
        </p>
      )}

      {entries.length === 0 ? (
        <p className="py-6 text-center text-sm text-muted-foreground">
          <T>Žiadne zaznamenané akcie.</T>
        </p>
      ) : (
        <div className="max-h-80 overflow-y-auto divide-y divide-border/40">
          {entries.map((entry) => (
            <div key={entry.id} className="flex items-center gap-3 py-2" data-entry={entry.action}>
              <div
                className={cn(
                  'w-7 h-7 rounded-md flex items-center justify-center shrink-0',
                  ACTIVITY_COLORS[actionColor(entry.action)],
                )}
              >
                <Activity className="w-3.5 h-3.5" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold leading-tight">
                  <span>{actionLabel(entry.action)}</span>
                  {!isNamedAction(entry.action) && (
                    <span className="ml-1 text-[10px] font-normal text-amber-500">
                      <T>neznáme</T>
                    </span>
                  )}
                  <span className="text-muted-foreground"> · </span>
                  <span className="font-mono text-xs">{entry.entityType}</span>
                  {entry.entityId !== null && (
                    <span className="text-muted-foreground text-xs"> #{entry.entityId}</span>
                  )}
                </p>
                <p className="text-[10px] text-muted-foreground">
                  {relativeTime(entry.createdAt, now) || formatDay(entry.createdAt)}
                </p>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

const ACTIVITY_COLORS: Record<string, string> = {
  emerald: 'bg-emerald-500/15 text-emerald-400',
  blue: 'bg-blue-500/15 text-blue-400',
  red: 'bg-red-500/15 text-red-400',
  violet: 'bg-violet-500/15 text-violet-400',
  orange: 'bg-orange-500/15 text-orange-400',
  amber: 'bg-amber-500/15 text-amber-400',
  cyan: 'bg-cyan-500/15 text-cyan-400',
  zinc: 'bg-muted text-muted-foreground',
}

// ─── performance ─────────────────────────────────────────────────────────────

function RangeBar({
  range,
  onChange,
  busy,
}: {
  range: Range
  onChange: (next: Range) => void
  busy: boolean
}) {
  return (
    <div className="bg-card border border-border rounded-2xl p-4 flex flex-wrap items-end gap-3">
      <label className="flex flex-col gap-1">
        <span className="text-[11px] font-bold text-muted-foreground uppercase tracking-wider">
          <T>Od</T>
        </span>
        <input
          type="date"
          value={range.from}
          onChange={(event) => onChange({ ...range, from: event.target.value })}
          aria-label={tt('Od dátumu')}
          data-range-from
          className="px-3 py-2 rounded-xl bg-background border border-border text-sm"
        />
      </label>
      <label className="flex flex-col gap-1">
        <span className="text-[11px] font-bold text-muted-foreground uppercase tracking-wider">
          <T>Do</T>
        </span>
        <input
          type="date"
          value={range.to}
          onChange={(event) => onChange({ ...range, to: event.target.value })}
          aria-label={tt('Do dátumu')}
          data-range-to
          className="px-3 py-2 rounded-xl bg-background border border-border text-sm"
        />
      </label>
      {busy && <Spinner className="w-4 h-4 mb-3" />}
    </div>
  )
}

function PerformanceTab({ role }: { role: string }) {
  const [range, setRange] = useState<Range>(() => ({ from: startOfMonth(), to: toDateParam(new Date()) }))
  const { data, isLoading } = useGetAuthMeStats(range)
  const performance = useMemo(() => performanceOf(data), [data])

  return (
    <div className="space-y-4">
      <RangeBar range={range} onChange={setRange} busy={isLoading} />

      {performance === null ? (
        <div className="flex justify-center py-16">
          <Spinner />
        </div>
      ) : (
        <>
          {/* FB-175 - said above the zeroes, not discovered from them. */}
          {attributionNote(performance) !== '' && (
            <p className="flex items-start gap-1.5 text-xs text-amber-500" data-attribution>
              <AlertTriangle className="w-4 h-4 shrink-0 mt-px" />
              {attributionNote(performance)}
            </p>
          )}

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-performance>
            <StatCard
              label={orderCountLabel(role)}
              value={ordersAreAttributed(role) ? formatCount(performance.orderCount) : tt('nemerané')}
              icon={ShoppingBag}
              color="primary"
            />
            <StatCard
              label={tt('Tržba spolu')}
              value={ordersAreAttributed(role) ? formatCents(performance.revenueCents) : tt('nemerané')}
              icon={Wallet}
              color="emerald"
            />
            <StatCard
              label={tt('Priemer / objednávka')}
              value={
                ordersAreAttributed(role) && performance.orderCount > 0
                  ? formatCents(performance.averageOrderCents)
                  : tt('nemerané')
              }
              icon={TrendingUp}
              color="blue"
            />
            <StatCard
              label={tt('€ / hodina práce')}
              value={moneyLabel(revenuePerHourCents(performance))}
              icon={Clock}
              color="amber"
            />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div className="bg-card border border-border rounded-2xl p-5">
              <h3 className="font-black flex items-center gap-2 mb-3">
                <CreditCard className="w-4 h-4 text-primary" /> <T>Platobné metódy</T>
              </h3>
              {performancePayments(performance).map((payment) => (
                <div key={payment.key} className="mb-3 last:mb-0" data-perf-payment={payment.key}>
                  <div className="flex items-center gap-2 text-xs mb-1">
                    <span className="font-bold flex-1">{payment.label}</span>
                    <span className="text-muted-foreground">
                      {payment.percent === null ? tt('nemerané') : `${Math.round(payment.percent)} %`}
                    </span>
                    <span className="font-black tabular-nums">{formatCents(payment.cents)}</span>
                  </div>
                  <div className="h-1.5 rounded-full bg-muted overflow-hidden">
                    <div
                      className="h-full bg-primary transition-all"
                      style={{ width: `${payment.percent ?? 0}%` }}
                    />
                  </div>
                </div>
              ))}
            </div>

            <div className="bg-card border border-border rounded-2xl p-5 space-y-3">
              <h3 className="font-black flex items-center gap-2">
                <Clock className="w-4 h-4 text-primary" /> <T>Práca v období</T>
              </h3>
              <Line label={tt('Odpracovaných hodín')} value={formatMinutes(performance.workedMinutes)} />
              <Line label={tt('Dokončených zmien')} value={formatCount(performance.shiftCount)} />
              <Line
                label={tt('Tržba / odpracovaná hodina')}
                value={moneyLabel(revenuePerHourCents(performance))}
                highlight
              />
              <Line
                label={tt('Tržba / zmena (priemerne)')}
                value={moneyLabel(revenuePerShiftCents(performance))}
              />
            </div>
          </div>
        </>
      )}
    </div>
  )
}

function Line({ label, value, highlight }: { label: string; value: string; highlight?: boolean }) {
  return (
    <div
      className={cn(
        'flex items-center justify-between px-3 py-2 rounded-lg',
        highlight ? 'bg-primary/5 border border-primary/20' : 'border-b border-border/40 last:border-0',
      )}
    >
      <span className="text-sm text-muted-foreground">{label}</span>
      <span className={cn('font-black tabular-nums', highlight ? 'text-primary text-lg' : 'text-sm')}>
        {value}
      </span>
    </div>
  )
}

// ─── attendance ──────────────────────────────────────────────────────────────

function AttendanceTab() {
  const [range, setRange] = useState<Range>(() => ({ from: startOfMonth(), to: toDateParam(new Date()) }))
  const { data, isLoading } = useGetAttendanceSelfHistory(range)

  const records = useMemo(() => recordsOf(data), [data])
  const summary = useMemo(() => summaryOf(data), [data])
  const real = realShifts(records)
  const instant = instantShifts(records)
  const average = averageShiftMinutes(records)

  return (
    <div className="space-y-4">
      <RangeBar range={range} onChange={setRange} busy={isLoading} />

      {summary !== null && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-attendance-summary>
          <StatCard label={tt('Odpracované')} value={formatMinutes(summary.totalMinutes)} icon={Clock} color="primary" />
          <StatCard label={tt('Dní so záznamom')} value={formatCount(summary.days)} icon={Clock} color="blue" />
          <StatCard
            label={tt('Skutočných zmien')}
            value={`${formatCount(real.length)} / ${formatCount(summary.completedShifts)}`}
            icon={CheckCircle2}
            color={instant.length > 0 ? 'amber' : 'emerald'}
            sub={instant.length > 0 ? tt('zvyšok trvá menej než minútu') : undefined}
          />
          <StatCard
            label={tt('Priemer / zmena')}
            value={average === null ? tt('nemerané') : formatMinutes(average)}
            icon={Clock}
            color="amber"
          />
        </div>
      )}

      {/* FB-176 - a 67-millisecond record is not a shift, and the average says so. */}
      {attendanceNote(records) !== '' && (
        <p className="flex items-start gap-1.5 text-xs text-amber-500" data-instant-note>
          <AlertTriangle className="w-4 h-4 shrink-0 mt-px" />
          {attendanceNote(records)}
        </p>
      )}

      <div className="bg-card border border-border rounded-2xl overflow-hidden">
        <div className="px-5 py-4 border-b border-border">
          <h3 className="font-black">
            <T>Záznamy</T>
          </h3>
        </div>
        {records.length === 0 ? (
          <p className="py-10 text-center text-sm text-muted-foreground">
            <T>Žiadne záznamy v tomto období.</T>
          </p>
        ) : (
          <div className="divide-y divide-border">
            {records.map((record) => (
              <RecordRow key={record.id} record={record} />
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

function RecordRow({ record }: { record: AttendanceRecord }) {
  const state = shiftState(record)
  const minutes = shiftMinutes(record)

  return (
    <div className="flex items-center gap-4 px-5 py-3" data-record={record.workDate} data-shift-state={state}>
      <div className="w-14 text-center shrink-0">
        <p className="text-2xl font-black tabular-nums leading-none">
          {Number(record.workDate.split('-')[2])}
        </p>
        <p className="text-[10px] text-muted-foreground uppercase tracking-wider mt-1">
          {record.workDate.slice(5, 7)}
        </p>
      </div>
      <div className="flex-1 flex items-center gap-4 flex-wrap">
        <span className="flex items-center gap-1.5 text-xs">
          <LogIn className="w-3 h-3 text-emerald-400" />
          <span className="font-bold text-emerald-400">
            {record.checkIn === null ? '-' : formatTime(record.checkIn)}
          </span>
        </span>
        <span className="flex items-center gap-1.5 text-xs">
          <LogOut className="w-3 h-3 text-orange-400" />
          <span className={cn('font-bold', record.checkOut === null ? 'text-muted-foreground/40' : 'text-orange-400')}>
            {record.checkOut === null ? '-' : formatTime(record.checkOut)}
          </span>
        </span>
        {record.notes !== null && (
          <span className="text-[11px] text-muted-foreground italic truncate max-w-md">{record.notes}</span>
        )}
      </div>
      <div className="text-right">
        {state === 'open' ? (
          <StatusBadge label={tt('Otvorená zmena')} color="amber" />
        ) : state === 'instant' ? (
          <StatusBadge label={tt('Príchod aj odchod naraz')} color="orange" />
        ) : state === 'backwards' ? (
          <StatusBadge label={tt('Odchod pred príchodom')} color="red" />
        ) : state === 'worked' ? (
          <span className="font-black text-sm tabular-nums">{formatMinutes(minutes)}</span>
        ) : (
          <span className="text-xs text-muted-foreground">-</span>
        )}
      </div>
    </div>
  )
}

// ─── small pieces ────────────────────────────────────────────────────────────

const INPUT =
  'w-full px-3 py-2.5 rounded-xl bg-background border border-border text-sm outline-none focus:ring-2 focus:ring-primary/20 transition-all'

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="text-[11px] font-bold text-muted-foreground uppercase tracking-wider mb-1.5 block">
        {label}
      </span>
      {children}
    </label>
  )
}
